import React from 'react';
import i18n from 'util/i18n';
import { educationRequiresPhysicalLocationAndReference, educationRequiresReference } from 'models/education';
import * as formTypes from 'constants/formTypes';
import { HistoryEducationItemModel } from 'forms/sections/history/education/types';
import ValidationElement, {
  ValidationElementProps,
} from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnError, CommonOnUpdate, CommonScrollIntoView, CommonRequired
} from 'types/props';
import { Dispatch } from 'redux';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Field from 'components/Form/Field';
import Checkbox from 'components/Form/Checkbox';
import DateRange from 'components/Form/DateRange';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Name from 'components/Form/Name';
import Show from 'components/Form/Show';
import NotApplicable from 'components/Form/NotApplicable';
import PVQLocation from 'components/Form/PVQLocation';
import Telephone from 'components/Form/Telephone';
import Email from 'components/Form/Email';
import BranchCollection from 'components/Form/BranchCollection';
import Location from 'components/Form/Location';
import Text from 'components/Form/Text';
import Comments from 'components/Form/Comments';
import InstitutionAutoComplete from 'components/Form/InstitutionAutoComplete';
import { isPVQForm } from 'helpers/form';
import Textarea from 'components/Form/Textarea';
import LocationCollection from 'components/Form/PVQLocation/LocationCollection';
import PVQLocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import PVQName from 'components/Form/PVQName';
import TelephoneCollection from 'components/Form/Telephone/TelephoneCollection';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';
import { educationVerifierTypes } from 'constants/enums/educationOptions';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import { CountryArrayModel, CountryModel, LocationModel, YesOrNoModel } from 'forms/shared/types';
import DiplomaItem from './Diploma';
import { getCountryValue, isSafeInternational } from 'helpers/locations';

interface Props
  extends Partial<HistoryEducationItemModel>,
  ValidationElementProps,
  AccordionItemProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  scrollIntoView?: CommonScrollIntoView
  required?: CommonRequired
  // TODO: Remove
  dispatch?: Dispatch
  // TODO: Write type
  addressBooks?: any

  g: formTypes.FormTypes

  attribute?: string
  isHistoricalDegree?: boolean
}

/**
 * Education item in a collection
 *
 * This was broken apart so it could manage minor local state in determining
 * when particular portions of this should be rendered.
 */
export default class EducationItem extends ValidationElement<Props> {
  static defaultProps: Partial<Props> = {
    Dates: {} as HistoryEducationItemModel['Dates'],
    Type: {} as HistoryEducationItemModel['Type'],
    Name: {} as HistoryEducationItemModel['Name'],
    Address: {} as HistoryEducationItemModel['Address'],
    Comments: {} as HistoryEducationItemModel['Comments'],
    ReferenceName: {} as HistoryEducationItemModel['ReferenceName'],
    ReferenceNameNotApplicable: { applicable: true },
    ReferencePhone: {} as HistoryEducationItemModel['ReferencePhone'],
    ReferencePhoneNotApplicable: { applicable: true },
    ReferenceEmail: {} as HistoryEducationItemModel['ReferenceEmail'],
    ReferenceEmailNotApplicable: { applicable: true },
    ReferenceAddress: {} as HistoryEducationItemModel['ReferenceAddress'],
    ReferenceAddressNotApplicable: { applicable: true },
    Diplomas: { branch: {} as YesOrNoModel, items: [] } as HistoryEducationItemModel['Diplomas'],
    LearningExperience: {} as HistoryEducationItemModel['LearningExperience'], // PVQ
    LearningExperienceOtherExplanation: {} as HistoryEducationItemModel['LearningExperienceOtherExplanation'], // PVQ
    OutOfBusiness: {} as HistoryEducationItemModel['OutOfBusiness'], // PVQ
    PhysicalLocations: {} as HistoryEducationItemModel['PhysicalLocations'], // PVQ
    ReferenceRelationship: {} as HistoryEducationItemModel['ReferenceRelationship'], // PVQ
    ReferenceRelationshipOtherExplanation: {} as HistoryEducationItemModel['ReferenceRelationshipOtherExplanation'], // PVQ
    ReferenceEmails: {} as HistoryEducationItemModel['ReferenceEmails'], // PVQ
    ReferencePhones: {} as HistoryEducationItemModel['ReferencePhones'], // PVQ
    TypeOtherExplanation: {} as HistoryEducationItemModel['TypeOtherExplanation'], // PVQ
    onUpdate: () => { },
    onError: (value, arr) => arr,
    isHistoricalDegree: false,
  };

  handleNameError = (value, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (queue: Partial<HistoryEducationItemModel>) => {
    const newQueue = { ...queue };
    if ((newQueue.Name && !newQueue.Address) && newQueue.Name?.value !== this.props.Name?.value) {
      newQueue.Address = {
        country: 'United States',
        state: '',
        city: '',
        street: '',
        street2: '',
        zipcode: '',
        county: '',
        countryComments: '',
        layout: 'Address',
        validated: false
      };
    }

    this.props.onUpdate({
      Dates: this.props.Dates,
      Type: this.props.Type,
      TypeOtherExplanation: this.props.TypeOtherExplanation,
      Name: this.props.Name,
      Address: this.props.Address,
      Comments: this.props.Comments,
      DiplomasAnswerComments: this.props.DiplomasAnswerComments,
      DegreesAwardedComments: this.props.DegreesAwardedComments,
      ReferenceComments: this.props.ReferenceComments,
      ReferenceName: this.props.ReferenceName,
      ReferenceNameNotApplicable: this.props.ReferenceNameNotApplicable,
      ReferenceType: this.props.ReferenceType,
      ReferenceTypeOtherExplanation: this.props.ReferenceTypeOtherExplanation,
      ReferencePhone: this.props.ReferencePhone,
      ReferencePhones: this.props.ReferencePhones,
      ReferencePhoneNotApplicable: this.props.ReferencePhoneNotApplicable,
      ReferenceEmail: this.props.ReferenceEmail,
      ReferenceEmails: this.props.ReferenceEmails,
      ReferenceEmailNotApplicable: this.props.ReferenceEmailNotApplicable,
      ReferenceAddress: this.props.ReferenceAddress,
      ReferenceAddressNotApplicable: this.props.ReferenceAddressNotApplicable,
      ReferenceRelationship: this.props.ReferenceRelationship,
      ReferenceRelationshipOtherExplanation: this.props.ReferenceRelationshipOtherExplanation,
      Diplomas: this.props.Diplomas,
      LearningExperience: this.props.LearningExperience,
      LearningExperienceOtherExplanation: this.props.LearningExperienceOtherExplanation,
      OutOfBusiness: this.props.OutOfBusiness,
      PhysicalLocations: this.props.PhysicalLocations,
      ...newQueue,
    });
  };

  updateDates = (values: HistoryEducationItemModel['Dates']) => {
    const dates = values || {};
    const requireReference = educationRequiresReference(dates);

    const {
      ReferenceName,
      ReferenceNameNotApplicable,
      ReferencePhone,
      ReferencePhoneNotApplicable,
      ReferenceEmail,
      ReferenceEmailNotApplicable,
      ReferenceAddress,
      ReferenceAddressNotApplicable,
    } = this.props;

    this.update({
      Dates: values,
      ReferenceName: requireReference ? ReferenceName : {} as HistoryEducationItemModel['ReferenceName'],
      ReferenceNameNotApplicable: requireReference ? ReferenceNameNotApplicable : { applicable: true },
      ReferencePhone: requireReference ? ReferencePhone : {} as HistoryEducationItemModel['ReferencePhone'],
      ReferencePhoneNotApplicable: requireReference ? ReferencePhoneNotApplicable : { applicable: true },
      ReferenceEmail: requireReference ? ReferenceEmail : {} as HistoryEducationItemModel['ReferenceEmail'],
      ReferenceEmailNotApplicable: requireReference ? ReferenceEmailNotApplicable : { applicable: true },
      ReferenceAddress: requireReference ? ReferenceAddress : {} as HistoryEducationItemModel['ReferenceAddress'],
      ReferenceAddressNotApplicable: requireReference ? ReferenceAddressNotApplicable : { applicable: true },
      Diplomas: this.getUpdatedDiplomas(this.props.Diplomas, dates)
    });
  };

  updateType = (values: HistoryEducationItemModel['Type']) => {
    this.update({
      Type: values,
    });
  };

  updateName = (values: HistoryEducationItemModel['Name']) => {
    this.update({
      Name: values,
    });
  };

  updateAddress = (values: HistoryEducationItemModel['Address']) => {
    this.update({
      Address: values,
    });
  };

  updatePhysicalAddressList = (
    values: HistoryEducationItemModel['PhysicalLocations']
  ) => {
    this.update({
      PhysicalLocations: values,
    });
  };

  updateReferenceName = (values: HistoryEducationItemModel['ReferenceName']) => {
    this.update({
      ReferenceName: values,
      ReferenceNameNotApplicable: { applicable: true },
    });
  };

  updateReferenceNameNotApplicable = (
    values: HistoryEducationItemModel['ReferenceNameNotApplicable']
  ) => {
    this.update({
      ReferenceName: values.applicable ? this.props.ReferenceName : {},
      ReferenceNameNotApplicable: values,
    });
  };

  updateReferencePhone = (values: HistoryEducationItemModel['ReferencePhone']) => {
    this.update({
      ReferencePhone: values,
      ReferencePhoneNotApplicable: values.noNumber ? { applicable: false } : { applicable: true }
    });
  };

  updateReferencePhoneNotApplicable = (
    values: HistoryEducationItemModel['ReferencePhoneNotApplicable']
  ) => {
    this.update({
      ReferencePhone: values.applicable ? this.props.ReferencePhone : {},
      ReferencePhoneNotApplicable: values,
    });
  };

  updateReferenceType = (values: HistoryEducationItemModel['ReferenceType']) => {
    const referenceType = values?.value;
    const selected = [...((this.props.ReferenceType || {}).values || [])];
    if (selected.includes(referenceType)) {
      selected.splice(selected.indexOf(referenceType), 1);
    } else {
      selected.push(referenceType);
    }

    this.update({
      ReferenceType: { values: selected },
    });
  };

  updateReferenceTypeOtherExplanation = (values: HistoryEducationItemModel['ReferenceTypeOtherExplanation']) => {
    this.update({
      ReferenceTypeOtherExplanation: values
    });
  };

  updateReferencePhones = (
    values: HistoryEducationItemModel['ReferencePhones']
  ) => {
    this.update({
      ReferencePhones: values,
    });
  };

  updateReferenceRelationshipOtherExplanation = (
    values: HistoryEducationItemModel['ReferenceRelationshipOtherExplanation']
  ) => {
    this.update({
      ReferenceRelationshipOtherExplanation: values,
    });
  };

  updateReferenceRelationship = (
    values: HistoryEducationItemModel['ReferenceRelationship']
  ) => {
    this.update({
      ReferenceRelationship: values,
    });
  };

  updateReferenceEmail = (values: HistoryEducationItemModel['ReferenceEmail']) => {
    this.update({
      ReferenceEmail: values,
      ReferenceEmailNotApplicable: { applicable: true },
    });
  };

  updateReferenceEmailNotApplicable = (
    values: HistoryEducationItemModel['ReferenceEmailNotApplicable']
  ) => {
    this.update({
      ReferenceEmail: values.applicable ? this.props.ReferenceEmail : {},
      ReferenceEmailNotApplicable: values,
    });
  };

  updateReferenceEmails = (
    values: HistoryEducationItemModel['ReferenceEmails']
  ) => {
    this.update({
      ReferenceEmails: values,
    });
  };

  updateReferenceAddress = (values: HistoryEducationItemModel['ReferenceAddress']) => {
    this.update({
      ReferenceAddress: values,
    });
  };

  updateReferenceAddressNotApplicable = (
    values: HistoryEducationItemModel['ReferenceAddressNotApplicable']
  ) => {
    this.update({
      ReferenceAddress: values.applicable ? this.props.ReferenceAddress : {},
      ReferenceAddressNotApplicable: values,
    });
  };

  updateReferenceAddressNotApplicablePVQ = (
    addressNotApplicable: HistoryEducationItemModel['ReferenceAddressNotApplicable']
  ) => {
    const queue: Partial<HistoryEducationItemModel> = {
      ReferenceAddressNotApplicable: { applicable: addressNotApplicable.applicable }
    };

    const referenceAddress: LocationModel | undefined = this.props.ReferenceAddress;

    let country: CountryModel | string | CountryArrayModel | undefined = referenceAddress?.country;

    if (country && !addressNotApplicable.applicable) {
      if (isSafeInternational(referenceAddress)) {
        country = { value: 'INTERNATIONAL' } as CountryModel;
      }

      queue.ReferenceAddress = { country } as LocationModel;
    }

    this.update(queue);
  };

  updateDiplomas = (values: HistoryEducationItemModel['Diplomas']) => {
    this.update({
      Diplomas: values
    });
  };

  updateComments = (values: HistoryEducationItemModel['Comments']) => {
    this.update({
      Comments: values
    });
  };

  updateReferenceComments = (values: HistoryEducationItemModel['ReferenceComments']) => {
    this.update({
      ReferenceComments: values
    });
  };

  updateDiplomasAnswerComments = (values: HistoryEducationItemModel['DiplomasAnswerComments']) => {
    this.update({
      DiplomasAnswerComments: values
    });
  };

  updateDegreesAwardedComments = (values: HistoryEducationItemModel['DegreesAwardedComments']) => {
    this.update({
      DegreesAwardedComments: values
    });
  };

  updateTypeOtherExplanation = (values: HistoryEducationItemModel['TypeOtherExplanation']) => {
    this.update({
      TypeOtherExplanation: values
    });
  };

  updateLearningExperience = (values: HistoryEducationItemModel['LearningExperience']) => {
    this.update({
      LearningExperience: values,
      LearningExperienceOtherExplanation: values?.value !== 'Other'
        ? { name: 'LearningExperienceOtherExplanation', value: '' } : this.props.LearningExperienceOtherExplanation,
    });
  };

  updateLearningExperienceOtherExplanation = (values: HistoryEducationItemModel['LearningExperienceOtherExplanation']) => {
    this.update({
      LearningExperienceOtherExplanation: values
    });
  };

  updateOutOfBusiness = (values: HistoryEducationItemModel['OutOfBusiness']) => {
    this.update({
      OutOfBusiness: values
    });
  };

  onInstitutionSuggestionSelected = (event, options, selectedInstitution) => {
    let institutionAddress: HistoryEducationItemModel['Address'] = {
      country: 'United States',
      state: '',
      city: '',
      street: '',
      street2: '',
      zipcode: '',
      validated: false
    };
    if (selectedInstitution?.city) {
      institutionAddress = {
        country: selectedInstitution?.country === 'USA' ? 'United States' : (selectedInstitution?.country || ''),
        state: selectedInstitution?.statecode || '',
        city: selectedInstitution?.city || '',
        street: selectedInstitution?.street1 || '',
        street2: selectedInstitution?.street2 || '',
        zipcode: selectedInstitution?.zipcode || '',
        validated: false
      };
    }
    if (selectedInstitution) {
      this.update({
        Address: institutionAddress,
        Name: { ...this.props.Name, value: (selectedInstitution.value || '') }
      });
    }
  };

  getUpdatedDiplomas = (values: HistoryEducationItemModel['Diplomas'], dates: HistoryEducationItemModel['Dates']): HistoryEducationItemModel['Diplomas'] => {
    const updatededItems = values.items?.map((item) => ({
      Item: {
        ...item.Item,
        Dates: dates || this.props.Dates
      }
    }));
    return { ...values, items: updatededItems || [] };
  };

  branchComment = () => {
    const attribute = `List.items[${this.props.$index}].Item`;
    return (
      <Comments
        title={i18n.t(`history.education.label.diplomasanswercomments`)}
        name="DiplomasAnswerComments"
        attribute={`${attribute}.DiplomasAnswerComments`}
        className="optional-comments"
        {...this.props.DiplomasAnswerComments}
        onUpdate={this.updateDiplomasAnswerComments}
        onError={this.props.onError}
        required={false}
      />
    );
  };

  render() {
    // Certain elements are present if the date range of the attendance was
    // within the last 3 years.
    const { Dates = {} } = this.props;
    const requireReference = educationRequiresReference(Dates);
    const requiresPhysicalLocationAndReference = educationRequiresPhysicalLocationAndReference(Dates);
    const baseAttribute = this.props.isHistoricalDegree ? 'ListDegrees' : 'List';
    const attribute = `${baseAttribute}.items[${this.props.$index}].Item`;

    const pvqEducationName = this.props.isHistoricalDegree ? i18n.t('history.pvq.education.heading.degreeSchoolName') : i18n.t('history.pvq.education.heading.name');
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="education">
        <div className="content">
          <Field
            title={pvqEducationName}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          >
            {this.props.hidesomething
              && (
                <Text
                  name="Name"
                  {...this.props.Name}
                  className="school-name"
                  maxlength="100"
                  onUpdate={this.updateName}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.Name`}
                />
              )}
            <InstitutionAutoComplete
              name="Name"
              className="school-name-auto"
              attribute={`${attribute}.Name`}
              onUpdate={this.updateName}
              onError={this.props.onError}
              required={this.props.required}
              value={this.props.Name.value}
              onSuggestionSelected={this.onInstitutionSuggestionSelected}
              requireUnitedStates={false}
            />
            <Checkbox
              label={i18n.t('history.pvq.education.label.outOfBusiness')}
              value="Yes"
              checked={this.props.OutOfBusiness?.checked}
              className="outOfBusiness"
              onUpdate={this.updateOutOfBusiness}
              onError={this.props.onError}
            />
          </Field>

          <Field
            title={i18n.t('history.pvq.education.heading.type')}
            titleSize="h4"
            adjustFor="big-buttons"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Type`}
          >
            <RadioGroup
              className="type option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={(this.props.Type || {}).value}
              attribute={`${attribute}.Type`}
            >
              <Radio
                name="type-highschool"
                className="type-highschool"
                label={i18n.t('history.pvq.education.label.type.highschool')}
                value="High School"
                onUpdate={this.updateType}
                onError={this.props.onError}
              />
              <Radio
                name="type-college"
                className="type-college"
                label={i18n.t('history.pvq.education.label.type.college')}
                value="College"
                onUpdate={this.updateType}
                onError={this.props.onError}
              />
              <Radio
                name="type-vocational"
                className="type-vocational"
                label={i18n.t('history.pvq.education.label.type.vocational')}
                value="Vocational"
                onUpdate={this.updateType}
                onError={this.props.onError}
              />
              <Radio
                name="type-other"
                className="type-other"
                label={i18n.t('history.pvq.education.label.type.other')}
                value="Other"
                onUpdate={this.updateType}
                onError={this.props.onError}
              />
            </RadioGroup>
          </Field>
          {this.props.Type?.value === 'Other' && (
            <Field
              title={i18n.t('history.pvq.education.heading.typeOtherExplanation')}
              attribute={`${attribute}.TypeOtherExplanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                name="TypeOtherExplanation"
                attribute={`${attribute}.TypeOtherExplanation`}
                {...this.props.TypeOtherExplanation}
                className="education-other-explanation"
                onUpdate={this.updateTypeOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          )}

          <Field
            title={i18n.t('history.pvq.education.heading.learningExperience')}
            titleSize="h4"
            adjustFor="big-buttons"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.LearningExperience`}
          >
            <RadioGroup
              className="learning-experience option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={(this.props.LearningExperience || {}).value}
              attribute={`${attribute}.LearningExperience`}
            >
              <Radio
                name="learningExperience-inPerson"
                className="learningExperience-inPerson"
                label={i18n.t('history.pvq.education.label.learningExperience.inPerson')}
                value="In-person"
                onUpdate={this.updateLearningExperience}
                onError={this.props.onError}
              />
              <Radio
                name="learningExperience-inPerson"
                className="learningExperience-inPerson"
                label={i18n.t('history.pvq.education.label.learningExperience.notInPerson')}
                value="Not in-person"
                onUpdate={this.updateLearningExperience}
                onError={this.props.onError}
              />
              <Radio
                name="learningExperience-combination"
                className="learningExperience-combination"
                label={i18n.t('history.pvq.education.label.learningExperience.combination')}
                value="Combination"
                onUpdate={this.updateLearningExperience}
                onError={this.props.onError}
              />
              <Radio
                name="learningExperience-other"
                className="learningExperience-other"
                label={i18n.t('history.pvq.education.label.learningExperience.other')}
                value="Other"
                onUpdate={this.updateLearningExperience}
                onError={this.props.onError}
              />
            </RadioGroup>
          </Field>

          {this.props.LearningExperience?.value === 'Other' && (
            <Field
              title={i18n.t('history.pvq.education.heading.learningExperienceOtherExplanation')}
              attribute={`${attribute}.LearningExperienceOtherExplanation`}
              scrollIntoView={this.props.scrollIntoView}
            >
              <Textarea
                name="LearningExperienceOtherExplanation"
                attribute={`${attribute}.LearningExperienceOtherExplanation`}
                {...this.props.LearningExperienceOtherExplanation}
                className="education-learningexperience-other-explanation"
                onUpdate={this.updateLearningExperienceOtherExplanation}
                onError={this.props.onError}
                required={this.props.required}
              />
            </Field>
          )}

          <Field
            title={i18n.t('history.pvq.education.heading.dates')}
            titleSize="h4"
            help="history.education.help.dates"
            adjustFor="daterange"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Dates`}
          >
            <DateRange
              name="Dates"
              {...Dates}
              label={i18n.t('history.education.label.dates')}
              minDateEqualTo
              onUpdate={this.updateDates}
              onError={this.props.onError}
              required={this.props.required}
              hideFromDay
              hideToDay
              attribute={`${attribute}.Dates`}
            />
          </Field>

          <Field
            attribute={`${attribute}.Address`}
            optional
            adjustFor="address"
            scrollIntoView={this.props.scrollIntoView}
            shrink
            dataTestId="Address"
          >
            <PVQLocation
              name="Address"
              attribute={`${attribute}.Address`}
              {...this.props.Address}
              label={i18n.t(`history.pvq.education.label.address`)}
              domesticTitle={i18n.t(`history.pvq.education.label.addressDomesticTitle`)}
              foreignTitle={i18n.t(`history.pvq.education.label.addressForeignTitle`)}
              includeMilitaryStates
              layout={Location.ADDRESS}
              geocode
              addressBook="Education"
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              onUpdate={this.updateAddress}
              onError={this.props.onError}
              required={this.props.required}
              isPoBoxAllowed={false}

            />
          </Field>
          <BranchCollection
            attribute={`${attribute}.Diplomas`}
            label={i18n.t('history.pvq.education.heading.degree')}
            titleSize="h4"
            appendLabel={i18n.t('history.pvq.education.heading.degreeTail')}
            className="receive-degree"
            {...this.props.Diplomas}
            onUpdate={this.updateDiplomas}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <DiplomaItem
              attribute={`${attribute}.Diplomas`}
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </BranchCollection>
          {requiresPhysicalLocationAndReference && (
            <>
              <LocationCollection
                {...this.props.PhysicalLocations}
                addressBook="Education"
                addressBooks={this.props.addressBooks}
                appendFieldTitle={i18n.t('history.pvq.education.heading.physicalLocationsAppend')}
                attribute={`${attribute}.PhysicalLocations`}
                branchFieldTitle={i18n.t('history.pvq.education.heading.physicalLocations')}
                dispatch={this.props.dispatch}
                domesticTitle={i18n.t(`history.pvq.education.label.addressDomesticTitle`)}
                foreignTitle={i18n.t(`history.pvq.education.label.addressForeignTitle`)}
                geocode
                includeMilitaryStates
                isPoBoxAllowed={false}
                layout={Location.ADDRESS}
                locationlabel={i18n.t(`history.pvq.education.label.physicalAddress`)}
                name="PhysicalLocations"
                onError={this.props.onError}
                onUpdate={this.updatePhysicalAddressList}
                required={this.props.required}
                scrollIntoView={this.props.scrollIntoView}
                showMilitaryInstallation
                showApoFpoDpoPostcodeWhenForeign
                hideInstallationNameUnknownWhenForeign

              />
              {requiresPhysicalLocationAndReference && this.props.requireHistoryEducationReferenceFields && (
                <div className="reference">
                  <Field
                    attribute={`${attribute}.ReferenceName`}
                    title={i18n.t('history.pvq.education.heading.reference')}
                    titleSize="h4"
                    className="explanation-left"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <PVQName
                      attribute={`${attribute}.ReferenceName`}
                      exactAttribute
                      aria-label="Provide full name"
                      name="name"
                      {...this.props.ReferenceName}
                      onUpdate={this.updateReferenceName}
                      onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
                      required={this.props.required}
                      scrollIntoView={this.props.scrollIntoView}
                      showLastFirstMiddleNameOrder
                      showMiddleUnknown
                    />
                  </Field>
                  <Field
                    attribute={`${attribute}.ReferenceType`}
                    title={i18n.t('history.pvq.education.heading.referenceType')}
                    titleSize="h4"
                    className="explanation-left"
                    scrollIntoView={this.props.scrollIntoView}
                  >
                    <CheckboxGroup
                      attribute={`${attribute}.ReferenceType`}
                      className="option-list"
                      selectedValues={(this.props.ReferenceType || {}).values}
                      onError={this.props.onError}
                      required={this.props.required}
                    >
                      {educationVerifierTypes.map((educationVerifierType) => (
                        <Checkbox
                          name={educationVerifierType}
                          label={educationVerifierType}
                          value={educationVerifierType}
                          className="yes"
                          disabled={this.props.disabled}
                          readonly={this.props.readonly}
                          required={this.props.required}
                          onUpdate={this.updateReferenceType} // eslint-disable-line @typescript-eslint/unbound-method
                          onError={this.handleError}
                        />
                      ))}
                    </CheckboxGroup>
                  </Field>
                  <Show when={this.props?.ReferenceType?.values?.includes('Other')}>
                    <Field
                      attribute={`${attribute}.ReferenceTypeOtherExplanation`}
                      title={i18n.t('history.pvq.education.heading.referenceTypeOther')}
                      titleSize="label"
                      adjustFor="textarea"
                      scrollIntoView={this.props.scrollIntoView}
                    >
                      <Textarea
                        name="ReferenceTypeOtherExplanation"
                        attribute={`${attribute}.ReferenceTypeOtherExplanation`}
                        {...this.props.ReferenceTypeOtherExplanation}
                        className="education-reference-other-explanation"
                        onUpdate={this.updateReferenceTypeOtherExplanation}
                        onError={this.props.onError}
                        required={this.props.required}
                      />
                    </Field>
                  </Show>

                  <TelephoneCollection
                    onUpdate={this.updateReferencePhones}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.ReferencePhones`}
                    telephoneFieldTitle="What is their phone number? (You may list more than one.)"
                    hideInitialBranchQuestion
                    allowNotApplicable
                    showTimeOfDay
                    {...this.props.ReferencePhones}
                  />
                  <EmailCollection
                    onUpdate={this.updateReferenceEmails}
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.ReferenceEmails`}
                    emailFieldTitle="What is their email address? (You may list more than one.)"
                    showEmailUnknown
                    includeEmailType={false}
                    hasSecondaryAppendLabels
                    secondaryNoAppendLabel="No"
                    secondaryYesAppendLabel="Add"

                    {...this.props.ReferenceEmails}
                  />
                  <Field
                    attribute={`${attribute}.ReferenceAddress`}
                    optional
                    adjustFor="address"
                    scrollIntoView={this.props.scrollIntoView}
                    shrink
                  >
                    <PVQLocationUnknown
                      attribute={`${attribute}.ReferenceAddress`}
                      name="reference-address"
                      {...this.props.ReferenceAddress}
                      addressBooks={this.props.addressBooks}
                      addressBook="Reference"
                      addressUnknown={this.props.ReferenceAddressNotApplicable}
                      layout={Location.ADDRESS}
                      onUpdate={this.updateReferenceAddress} // eslint-disable-line @typescript-eslint/unbound-method
                      onUpdateAddressUnknown={this.updateReferenceAddressNotApplicablePVQ}
                      onError={this.props.onError}
                      dispatch={this.props.dispatch}
                      required={this.props.required}
                      geocode
                      showAddressUnknown
                      showInternationalStreet
                      showMilitaryInstallation
                      label={i18n.t(`history.pvq.education.verifier.address`)}
                      domesticTitle={i18n.t(`history.pvq.education.verifier.addressDomesticTitle`)}
                      foreignTitle={i18n.t(`history.pvq.education.verifier.addressForeignTitle`)}
                      hideInstallationNameUnknownWhenForeign
                      showApoFpoDpoPostcodeWhenForeign
                      showIsInstallationUnknownIfInternational
                      showIsInstallationUnknownIfDomestic
                    />
                  </Field>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    ) : (
      <div className="education">
        <div className="content">
          <Field
            title={i18n.t('history.education.heading.name')}
            titleSize="h4"
            adjustFor="labels"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Name`}
          >
            {this.props.hidesomething
              && (
                <Text
                  name="Name"
                  {...this.props.Name}
                  className="school-name"
                  maxlength="100"
                  onUpdate={this.updateName}
                  onError={this.props.onError}
                  required={this.props.required}
                  attribute={`${attribute}.Name`}
                />
              )}
            <InstitutionAutoComplete
              name="Name"
              className="school-name-auto"
              attribute={`${attribute}.Name`}
              onUpdate={this.updateName}
              onError={this.props.onError}
              required={this.props.required}
              value={this.props.Name.value}
              onSuggestionSelected={this.onInstitutionSuggestionSelected}
              requireUnitedStates={false}
            />

          </Field>

          <Field
            title={i18n.t('history.education.heading.dates')}
            titleSize="h4"
            help="history.education.help.dates"
            adjustFor="daterange"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Dates`}
          >
            <DateRange
              name="Dates"
              {...Dates}
              label={i18n.t('history.education.label.dates')}
              minDateEqualTo
              onUpdate={this.updateDates}
              onError={this.props.onError}
              required={this.props.required}
              hideFromDay
              hideToDay
              attribute={`${attribute}.Dates`}
            />
          </Field>

          <Field
            title={i18n.t('history.education.heading.address')}
            titleSize="h4"
            optional
            help="history.education.help.address"
            onError={this.props.onError}
            adjustFor="address"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Address`}
          >
            {/* eslint jsx-a11y/label-has-associated-control: 0 */}
            {/* eslint jsx-a11y/label-has-for: 0 */}
            <label className="into-label">{i18n.m('history.education.label.addressLink')}</label>
            <Location
              name="Address"
              {...this.props.Address}
              label={i18n.t('history.education.label.address')}
              layout={Location.ADDRESS}
              dispatch={this.props.dispatch}
              addressBooks={this.props.addressBooks}
              addressBook="Education"
              geocode
              showPostOffice
              onUpdate={this.updateAddress}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.Address`}
            />
          </Field>

          <Field
            title={i18n.t('history.education.heading.type')}
            titleSize="h4"
            adjustFor="big-buttons"
            shrink
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Type`}
          >
            <RadioGroup
              className="type option-list option-list-vertical"
              required={this.props.required}
              onError={this.props.onError}
              selectedValue={(this.props.Type || {}).value}
              attribute={`${attribute}.Type`}
            >
              <Radio
                name="type-highschool"
                className="type-highschool"
                label={i18n.t('history.education.label.type.highschool')}
                value="High School"
                onUpdate={this.updateType}
                onError={this.props.onError}
              />
              <Radio
                name="type-college"
                className="type-college"
                label={i18n.t('history.education.label.type.college')}
                value="College"
                onUpdate={this.updateType}
                onError={this.props.onError}
              />
              <Radio
                name="type-vocational"
                className="type-vocational"
                label={i18n.t('history.education.label.type.vocational')}
                value="Vocational"
                onUpdate={this.updateType}
                onError={this.props.onError}
              />
              {this.props.formType !== formTypes.SF852013 && (
                <Radio
                  name="type-correspondence"
                  className="type-correspondence"
                  label={i18n.t('history.education.label.type.correspondence')}
                  value="Correspondence"
                  onUpdate={this.updateType}
                  onError={this.props.onError}
                />
              )}
            </RadioGroup>
          </Field>
          <Comments
            title={i18n.t('history.education.label.comments')}
            name="Comments"
            className="optional-comments"
            attribute="Comments"
            {...this.props.Comments}
            onUpdate={this.updateComments}
            required={false}
          />

          {requireReference && this.props.requireHistoryEducationReferenceFields && (
            <div className="reference">
              <Field
                title={i18n.t('history.education.heading.reference')}
                titleSize="h3"
                className="no-margin-bottom"
                scrollIntoView={this.props.scrollIntoView}
              >
                {i18n.t('history.education.para.reference')}
              </Field>

              <Field
                title={i18n.t('reference.heading.name')}
                titleSize="h4"
                optional
                filterErrors={Name.requiredErrorsOnly}
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ReferenceName`}
              >
                <NotApplicable
                  name="ReferenceNameNotApplicable"
                  {...this.props.ReferenceNameNotApplicable}
                  label={i18n.t('reference.label.idk')}
                  or={i18n.t('reference.para.or')}
                  onUpdate={this.updateReferenceNameNotApplicable}
                  onError={this.props.onError}
                  attribute={`${attribute}.ReferenceNameNotApplicable`}
                >
                  <Name
                    attribute={`${attribute}.ReferenceName`}
                    name="ReferenceName"
                    aria-label="List a person who can verify that you attended this school"
                    prefix="name"
                    className="reference-name"
                    {...this.props.ReferenceName}
                    scrollIntoView={this.props.scrollIntoView}
                    hideMiddleName
                    onUpdate={this.updateReferenceName}
                    onError={this.handleNameError}
                    required={this.props.required}
                  />
                </NotApplicable>
              </Field>

              <Field
                title={i18n.t('reference.heading.correspondence')}
                titleSize="h4"
                optional
                className="no-margin-bottom"
                scrollIntoView={this.props.scrollIntoView}
              >
                {i18n.t('reference.para.correspondence')}
              </Field>
              {this.props.formType !== formTypes.SF852013 && (
                <Field
                  title={i18n.t('reference.heading.phone.default')}
                  titleSize="h4"
                  className="override-required"
                  help="reference.help.phone"
                  adjustFor="telephone"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ReferencePhone`}
                >
                  <Telephone
                    name="ReferencePhone"
                    className="reference-phone"
                    {...this.props.ReferencePhone}
                    onUpdate={this.updateReferencePhone}
                    allowNotApplicable
                    onError={this.props.onError}
                    required={this.props.required}
                    attribute={`${attribute}.ReferencePhone`}
                  />
                </Field>
              )}
              <Field
                title={i18n.t('reference.heading.email')}
                titleSize="h4"
                help="reference.help.email"
                adjustFor="label"
                scrollIntoView={this.props.scrollIntoView}
                attribute={`${attribute}.ReferenceEmail`}
              >
                <NotApplicable
                  name="ReferenceEmailNotApplicable"
                  {...this.props.ReferenceEmailNotApplicable}
                  label={i18n.t('reference.label.idk')}
                  or={i18n.t('reference.para.or')}
                  onUpdate={this.updateReferenceEmailNotApplicable}
                  onError={this.props.onError}
                  attribute={`${attribute}.ReferenceEmailNotApplicable`}
                >
                  <Email
                    name="ReferenceEmail"
                    {...this.props.ReferenceEmail}
                    className="reference-email"
                    label={i18n.t('reference.label.email')}
                    onUpdate={this.updateReferenceEmail}
                    onError={this.props.onError}
                    attribute={`${attribute}.ReferenceEmail`}
                  />
                </NotApplicable>
              </Field>
              {this.props.formType !== formTypes.SF852013 && (
                <Field
                  title={i18n.t('reference.heading.address')}
                  titleSize="h4"
                  optional
                  help="reference.help.address"
                  adjustFor="address"
                  scrollIntoView={this.props.scrollIntoView}
                  attribute={`${attribute}.ReferenceAddress`}
                >
                  <NotApplicable
                    name="ReferenceAddressNotApplicable"
                    {...this.props.ReferenceAddressNotApplicable}
                    label={i18n.t('reference.label.idk')}
                    or={i18n.t('reference.para.or')}
                    onUpdate={this.updateReferenceAddressNotApplicable}
                    onError={this.props.onError}
                    attribute={`${attribute}.ReferenceAddressNotApplicable`}
                  >
                    <p>{i18n.t('reference.para.address')}</p>
                    <Location
                      name="ReferenceAddress"
                      className="reference-address"
                      {...this.props.ReferenceAddress}
                      label={i18n.t('reference.label.address')}
                      layout={Location.ADDRESS}
                      geocode
                      addressBooks={this.props.addressBooks}
                      addressBook="Reference"
                      showPostOffice
                      dispatch={this.props.dispatch}
                      onUpdate={this.updateReferenceAddress}
                      onError={this.props.onError}
                      attribute={`${attribute}.ReferenceAddress`}
                    />
                  </NotApplicable>
                </Field>
              )}
              <Comments
                title={i18n.t('history.education.label.referencecomments')}
                name="ReferenceComments"
                className="optional-comments"
                attribute="ReferenceComments"
                {...this.props.ReferenceComments}
                onUpdate={this.updateReferenceComments}
                required={false}
              />
            </div>
          )}

          <BranchCollection
            attribute={`${attribute}.Diplomas`}
            label={i18n.t('history.education.heading.degree')}
            titleSize="h4"
            appendLabel={i18n.t('history.education.heading.degreeTail')}
            className="receive-degree"
            {...this.props.Diplomas}
            onUpdate={this.updateDiplomas}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            branchComment={this.branchComment}
          >
            <DiplomaItem
              attribute={`${attribute}.Diplomas`}
              name="Item"
              bind
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
            <Comments
              title={i18n.t('history.education.label.degreesawardedcomments')}
              name="DegreesAwardedComments"
              className="optional-comments"
              attribute="DegreesAwardedComments"
              {...this.props.DegreesAwardedComments}
              onUpdate={this.updateDegreesAwardedComments}
              required={false}
            />
          </BranchCollection>
        </div>
      </div>
    );
  }
}
